import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Paperclip } from 'lucide-react';

import PageHeaderActions from '../components/PageHeaderActions';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Textarea } from '../components/ui/textarea';
import { client } from '../services/appwrite';
import {
  getSupportRequest,
  listSupportMessages,
  addSupportMessage,
  getSupportAttachmentUrl,
} from '../services/api';
import {
  SUPPORT_STATUS_LABELS,
  SUPPORT_STATUS_VARIANTS,
  supportTypeLabel,
  messageAuthorLabel,
  formatSupportDate,
} from './areas/support-display';

/** The internal-tools database. A fixed id — see .github/conventions.md. */
const INTERNAL_DATABASE_ID = 'panelgear-internal';

/**
 * One support request: what was asked, what came back, and a box to reply in.
 *
 * Only the fields that mean something to the person who raised it. HQ's triage fields exist
 * on the same row and are not shown; the ones that would actually matter if they were
 * (`aiSummary`, `wasAiDrafted`) are unpopulated until the AI desk work lands, and move to a
 * separate table as part of it.
 *
 * Internal notes are absent because they are unreadable from this session, not because
 * anything here filters them out — HQ writes them with no row permission.
 */
export default function SupportRequestDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);

  // Per conventions.md: state keyed by the active record, so switching between two requests
  // cannot leave the previous one's thread or half-typed reply on screen.
  const loadedIdRef = useRef(null);

  const load = useCallback(async (ticketId) => {
    setLoading(true);
    try {
      const [loadedTicket, loadedMessages] = await Promise.all([
        getSupportRequest(ticketId),
        listSupportMessages(ticketId),
      ]);
      setTicket(loadedTicket);
      setMessages(loadedMessages);
      setError('');
    } catch (loadError) {
      setTicket(null);
      setMessages([]);
      setError(
        loadError?.code === 404 || loadError?.code === 401
          ? 'That support request could not be found.'
          : (loadError?.message || 'That support request could not be loaded.'),
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!id || loadedIdRef.current === id) return;
    loadedIdRef.current = id;
    setReply('');
    load(id);
  }, [id, load]);

  // A reply from HQ should appear without a refresh. This works precisely because the app
  // reads the rows directly — a message written through a function would give the browser
  // nothing to subscribe to.
  useEffect(() => {
    if (!id) return undefined;

    let unsubscribe;
    try {
      unsubscribe = client.subscribe(
        `databases.${INTERNAL_DATABASE_ID}.collections.ticket_messages.documents`,
        (response) => {
          const row = response.payload;
          if (!row?.$id || row.ticketId !== id) return;
          if (!response.events.some((event) => event.endsWith('.create'))) return;
          setMessages((previous) => (
            previous.some((message) => message.$id === row.$id) ? previous : [...previous, row]
          ));
        },
      );
    } catch (subscribeError) {
      // A thread that does not live-update is a much smaller problem than one that will not
      // render, so this is logged and swallowed.
      console.error('[Support] realtime subscribe failed:', subscribeError.message);
    }

    return () => { if (typeof unsubscribe === 'function') unsubscribe(); };
  }, [id]);

  const handleReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await addSupportMessage(id, reply);
      setReply('');
      // The realtime handler usually beats this, and it de-duplicates by id when it does.
      setMessages(await listSupportMessages(id));
      toast.success('Reply sent');
    } catch (replyError) {
      toast.error(replyError?.message || 'That reply could not be sent.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <Button
            variant="ghost"
            size="icon"
            aria-label="Back to support requests"
            onClick={() => navigate('/support')}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="truncate text-3xl font-bold">
            {loading ? 'Support request' : (ticket?.subject || 'Support request')}
          </h1>
        </div>
        <PageHeaderActions searchClassName="w-64" />
      </div>

      {loading && <Skeleton className="h-64 w-full" />}

      {!loading && error && (
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-destructive">{error}</p>
          </CardContent>
        </Card>
      )}

      {!loading && !error && ticket && (
        <>
          <Card>
            <CardContent className="flex flex-wrap items-center gap-3 p-4 text-sm text-muted-foreground">
              <Badge variant={SUPPORT_STATUS_VARIANTS[ticket.status] || 'secondary'}>
                {SUPPORT_STATUS_LABELS[ticket.status] || ticket.status}
              </Badge>
              <span>{ticket.reference}</span>
              <span>·</span>
              <span>{supportTypeLabel(ticket.type)}</span>
              <span>·</span>
              <span>Raised {formatSupportDate(ticket.$createdAt)}</span>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Conversation</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {messages.map((message) => (
                <div
                  key={message.$id}
                  className={`rounded-lg p-4 ${
                    message.direction === 'outbound' ? 'bg-muted' : 'bg-accent/40'
                  }`}
                >
                  <div className="mb-2 flex items-center justify-between gap-3 text-sm">
                    <span className="font-medium">{messageAuthorLabel(message)}</span>
                    <span className="text-muted-foreground">
                      {formatSupportDate(message.$createdAt)}
                    </span>
                  </div>
                  <p className="whitespace-pre-wrap text-sm">{message.bodyText}</p>

                  {(message.attachmentFileIds || []).length > 0 && (
                    <ul className="mt-3 space-y-1">
                      {message.attachmentFileIds.map((fileId) => (
                        <li key={fileId}>
                          <a
                            href={getSupportAttachmentUrl(fileId)}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
                          >
                            <Paperclip className="h-3.5 w-3.5" />
                            Attachment
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}

              {messages.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  This request has no messages yet.
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Add a message</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Textarea
                value={reply}
                onChange={(event) => setReply(event.target.value)}
                placeholder="Anything else that would help us?"
                className="min-h-[120px]"
                disabled={sending}
                aria-label="Add a message to this request"
              />
              <div className="flex justify-end">
                <Button onClick={handleReply} disabled={sending || !reply.trim()}>
                  {sending ? 'Sending…' : 'Send'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
